package ru.chm.gradereport.ui.screens.course.sheet

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import ru.chm.gradereport.model.HomeworkDto
import ru.chm.gradereport.model.LessonSessionDto
import ru.chm.gradereport.platform.copyTextToClipboard
import ru.chm.gradereport.ui.components.AppCard
import ru.chm.gradereport.ui.components.AppTextField
import ru.chm.gradereport.ui.components.MutedText
import ru.chm.gradereport.ui.components.PrimaryButton
import ru.chm.gradereport.ui.components.SecondaryButton
import ru.chm.gradereport.ui.components.SelectableText
import ru.chm.gradereport.ui.components.SoftDeleteButton
import ru.chm.gradereport.ui.components.formatDisplayDate
import ru.chm.gradereport.ui.components.horizontalScrollOnWheel
import ru.chm.gradereport.ui.components.LinkedText
import ru.chm.gradereport.ui.screens.course.homeworkSubmissionApiUrl
import ru.chm.gradereport.ui.screens.course.trimScore
import ru.chm.gradereport.ui.theme.GradeTheme
import ru.chm.gradereport.ui.theme.YellowAccent

@Composable
internal fun HomeworkDetailsDialog(
    homework: HomeworkDto,
    sessions: List<LessonSessionDto>,
    session: LessonSessionDto?,
    editable: Boolean,
    isAdmin: Boolean = false,
    publicToken: String = "",
    formsBearerConfigured: Boolean = false,
    onDismiss: () -> Unit,
    onSave: (sessionId: String, title: String, description: String, maxScore: Double) -> Unit,
    onDelete: () -> Unit,
) {
    var selectedSessionId by remember(homework.id, homework.sessionId) {
        mutableStateOf(homework.sessionId.orEmpty())
    }
    var title by remember(homework.id, homework.title) { mutableStateOf(homework.title) }
    var description by remember(homework.id, homework.description) {
        mutableStateOf(homework.description)
    }
    var maxScore by remember(homework.id, homework.maxScore) {
        mutableStateOf(trimScore(homework.maxScore))
    }
    var copiedSubmitUrl by remember { mutableStateOf(false) }
    val sessionsNewestFirst = remember(sessions) {
        sessions.sortedWith(
            compareByDescending<LessonSessionDto> { it.date }
                .thenByDescending { it.title }
                .thenByDescending { it.id },
        )
    }
    val submitUrl = remember(isAdmin, publicToken, homework.id) {
        if (isAdmin && publicToken.isNotBlank()) {
            homeworkSubmissionApiUrl(publicToken, homework.id)
        } else {
            ""
        }
    }

    val parsedMax = maxScore.toDoubleOrNull()
    val canSave = editable &&
        title.isNotBlank() &&
        selectedSessionId.isNotBlank() &&
        parsedMax != null &&
        parsedMax > 0 &&
        (
            selectedSessionId != homework.sessionId.orEmpty() ||
                title.trim() != homework.title ||
                description.trim() != homework.description ||
                parsedMax != homework.maxScore
        )

    val colors = GradeTheme.colors
    Dialog(onDismissRequest = onDismiss) {
        AppCard(
            modifier = Modifier
                .widthIn(min = 320.dp, max = 480.dp)
                .verticalScroll(rememberScrollState()),
        ) {
            Text(
                "Домашнее задание",
                color = colors.textPrimary,
                fontWeight = FontWeight.Bold,
                fontSize = 20.sp,
            )

            if (editable) {
                Text(
                    "Занятие",
                    color = YellowAccent,
                    fontSize = 13.sp,
                    fontWeight = FontWeight.SemiBold,
                    modifier = Modifier.padding(top = 12.dp),
                )
                val sessionsScroll = rememberScrollState()
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .padding(top = 6.dp)
                        .horizontalScroll(sessionsScroll)
                        .horizontalScrollOnWheel(sessionsScroll),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                    sessionsNewestFirst.forEach { item ->
                        val selected = item.id == selectedSessionId
                        Text(
                            text = "${formatDisplayDate(item.date)} · ${item.title}",
                            color = if (selected) Color.White else colors.textMuted,
                            fontWeight = if (selected) FontWeight.Bold else FontWeight.Normal,
                            fontSize = 13.sp,
                            modifier = Modifier
                                .clip(RoundedCornerShape(10.dp))
                                .background(
                                    if (selected) SessionAccent.copy(alpha = 0.35f)
                                    else colors.nestedCard,
                                )
                                .border(
                                    1.dp,
                                    if (selected) SessionAccent else colors.border,
                                    RoundedCornerShape(10.dp),
                                )
                                .clickable { selectedSessionId = item.id }
                                .padding(horizontal = 12.dp, vertical = 8.dp),
                        )
                    }
                }
                AppTextField(
                    value = title,
                    onValueChange = { title = it },
                    label = "Название ДЗ",
                    modifier = Modifier.padding(top = 12.dp),
                )
                AppTextField(
                    value = description,
                    onValueChange = { description = it },
                    label = "Что задано (описание)",
                    modifier = Modifier.padding(top = 8.dp).heightIn(min = 96.dp),
                    singleLine = false,
                )
                AppTextField(
                    value = maxScore,
                    onValueChange = { maxScore = it },
                    label = "Макс. балл",
                    modifier = Modifier.padding(top = 8.dp),
                )
            } else {
                SelectableText(
                    text = homework.title,
                    color = colors.textPrimary,
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 18.sp,
                    modifier = Modifier.padding(top = 10.dp),
                )
                if (session != null) {
                    MutedText(
                        "Занятие: ${formatDisplayDate(session.date)}" +
                            if (session.title.isNotBlank()) " — ${session.title}" else "",
                        modifier = Modifier.padding(top = 6.dp),
                    )
                }
                Text(
                    "Задание",
                    color = colors.textPrimary,
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 15.sp,
                    modifier = Modifier.padding(top = 16.dp, bottom = 6.dp),
                )
                if (homework.description.isBlank()) {
                    MutedText("Описание не указано.")
                } else {
                    LinkedText(
                        text = homework.description,
                        color = colors.textPrimary.copy(alpha = 0.9f),
                        fontSize = 15.sp,
                        lineHeight = 22.sp,
                    )
                }
            }

            if (submitUrl.isNotBlank()) {
                Text(
                    "Форма (POST)",
                    color = colors.textPrimary,
                    fontWeight = FontWeight.SemiBold,
                    fontSize = 15.sp,
                    modifier = Modifier.padding(top = 16.dp, bottom = 6.dp),
                )
                MutedText(
                    "Тело: { \"uid\": \"...\", \"formUuid\": \"uuid-ответа\", " +
                        "\"payload\": { \"ключ\": \"значение\" } }",
                )
                if (formsBearerConfigured) {
                    MutedText(
                        "Заголовок: Authorization: Bearer <токен из настроек>",
                        modifier = Modifier.padding(top = 4.dp),
                    )
                }
                SelectableText(
                    text = submitUrl,
                    color = colors.textMuted,
                    fontSize = 14.sp,
                    modifier = Modifier.padding(top = 4.dp),
                )
                SecondaryButton(
                    text = if (copiedSubmitUrl) "URL скопирован" else "Скопировать URL отправки",
                    onClick = {
                        copyTextToClipboard(submitUrl)
                        copiedSubmitUrl = true
                    },
                    modifier = Modifier.padding(top = 8.dp).fillMaxWidth(),
                )
            }
            Row(
                modifier = Modifier.fillMaxWidth().padding(top = 16.dp),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                if (editable) {
                    SoftDeleteButton(
                        text = "Удалить",
                        onClick = onDelete,
                        modifier = Modifier.weight(1f),
                    )
                    PrimaryButton(
                        text = "Сохранить",
                        enabled = canSave,
                        onClick = {
                            val score = parsedMax ?: return@PrimaryButton
                            onSave(
                                selectedSessionId,
                                title.trim(),
                                description.trim(),
                                score,
                            )
                        },
                        modifier = Modifier.weight(1f),
                    )
                }
                SecondaryButton(
                    text = "Закрыть",
                    onClick = onDismiss,
                    modifier = Modifier.weight(1f),
                )
            }
        }
    }
}
